/**
 * ResConsumeExplainer — progressive-disclosure explainer for RES.CONSUME (R1).
 *
 * RES.CONSUME is the linear-category side of the RES.BALANCE dual pair. It
 * neutralizes the vulnerability class "resource consumed beyond budget" by
 * typing every cost-bearing operation as a linear morphism: a budget token
 * is consumed exactly once and cannot be duplicated, so unbounded
 * consumption is structurally inexpressible (CONTAINED mode).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { RES_CONSUME_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  LinearResourceLedger,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR["RES.CONSUME"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-res-consume",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "RES.CONSUME",
  subtitle: "R1 · Linear category · CONTAINED · G4 Quantity",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Linear category (structure)", href: "#explainer-linear-category", accent: ACCENT },
  {
    label: "RES.RELEASE (dual)",
    href: "#explainer-res-release",
    accent: THEOREM_COLOR["RES.RELEASE"],
  },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>RES.CONSUME</strong> says: every operation spends a budget token, and a token spends
      exactly once, by construction. You cannot write a step that consumes more than it was given.
    </p>
  ),
  intuition: (
    <>
      <p>
        Resource exhaustion — unbounded loops, fork bombs, amplification — is a quantitative bug:
        not "the wrong thing happened" but "too much of the right thing happened". A runtime quota
        watches a counter and trips when it overflows, a story about catching the overrun after the
        consuming code is expressible.
      </p>
      <p>
        CSK types each cost-bearing operation as a linear morphism over a cost monoid. A budget
        token is a linear resource: used once, never copied. There is no term that consumes a token
        twice, so consumption is bounded by the structure rather than by a counter — the damage is{" "}
        <em>contained</em> by construction.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Work in a linear category over a cost monoid{" "}
        <MathInline>{tex`(\mathrm{Cost}, +, 0)`}</MathInline>. A cost-bearing operation is a
        morphism{" "}
        <MathInline>{tex`A \otimes \mathrm{Budget}_{c} \to B \otimes \mathrm{Budget}_{c - k}`}</MathInline>{" "}
        that consumes <MathInline>{tex`k`}</MathInline> of the linear budget object. Linearity
        forbids the contraction{" "}
        <MathInline>{tex`\mathrm{Budget} \to \mathrm{Budget} \otimes \mathrm{Budget}`}</MathInline>.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Because the budget object has no diagonal, total consumption along any composite is the sum
        of the typed costs and cannot exceed the initial allocation. The over-budget composite is
        not a closed term. <code>RES.CONSUME</code> is a projection of the Categorical Immunity
        Theorem and forms the <code>RES.BALANCE</code> dual pair with <code>RES.RELEASE</code>.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> uncontrolled resource consumption —
        exhaustion, missing throttle, runaway recursion (CWE-400, CWE-770, CWE-834; e.g.,
        CVE-2021-45046). Realized by <em>structural inexpressibility</em> of the over-budget
        composite; the mode is <strong>CONTAINED</strong> — execution may run, but damage cannot
        exceed the structural budget.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you call an unmetered host primitive outside
        the linear category, you have left the kernel. RES.CONSUME is then vacuous on that call —
        the budget discipline has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {RES_CONSUME_META.seamType}. <strong>Bridge</strong>: budget override (governance artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "RES.CONSUME theorem card",
      path: "docs/reference/theorem-cards/RES-CONSUME.md",
    },
    {
      label: "Kernel module (main RES.CONSUME neutralization theorem)",
      path: `agda/${RES_CONSUME_META.agdaSource}`,
      note: `RES_CONSUME_META.agdaSource is "${RES_CONSUME_META.agdaSource}"; the repo path is prefixed with "agda/". Main theorem in module lines 80–150.`,
    },
    {
      label: "Theorem Canon (§4.3 CONTAINED; §6 RES.BALANCE)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="RES.CONSUME"
      code="R1"
      structure="Linear category"
      mode="contained"
      generator="G4"
      seamType={RES_CONSUME_META.seamType}
      bridgeArtifact="budget override"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <LinearResourceLedger
        accent={ACCENT}
        ariaLabel="Linear resource ledger: each budget token is consumed exactly once; no contraction lets a token be reused"
      />
    </div>
  </>
);

export function ResConsumeExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
